import { useAdvisorNavigation } from '@client/components/AdvisorDashboardRouteGuard';
import {
  fetchSessionForAuthRequest,
  selectOauth,
  selectSession,
  setOauthRedirectInitiated,
} from '@client/model';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ClipLoader } from 'react-spinners';
import { CheckCircle, TriangleAlert } from 'lucide-react';
import { useToast } from './hooks/use-toast';
import { capitalizeFirstLetter } from '@shared';

export enum OAuthRedirectPage {
  ADVISOR_PROFILE = 'advisorProfile',
  ADVISOR_ONBOARDING = 'advisorOnboarding',
  HOME = 'home',
}

export const OAUTH_REDIRECT_FROM_PAGE_KEY = 'oauth_redirect_from_page';

const getPageToNavigateFn = (
  pageToNavigateFn: Record<OAuthRedirectPage, () => void>,
): (() => void) => {
  const redirectPageKey = localStorage.getItem(
    OAUTH_REDIRECT_FROM_PAGE_KEY,
  ) as OAuthRedirectPage | null;

  if (!redirectPageKey || !pageToNavigateFn[redirectPageKey]) {
    return pageToNavigateFn[OAuthRedirectPage.HOME];
  }

  const redirectPageFn = pageToNavigateFn[redirectPageKey];
  return redirectPageFn;
};

export interface OAuthProviderConfig {
  provider: OAuthProvider;
  title: string;
  dispatchAction: (payload: { code: string; advisorId: string }) => any;
}

// Generic OAuth callback component
export function OAuthCallback({
  providerConfig,
}: {
  providerConfig: OAuthProviderConfig;
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateToAdvisorProfile } = useAdvisorNavigation();
  const { error, status, redirectInitiated } = useSelector(selectOauth);
  const session = useSelector(selectSession);
  const advisorId = session.advisor?.advisorId;
  const pageToNavigateFn: Record<OAuthRedirectPage, () => void> = {
    [OAuthRedirectPage.ADVISOR_PROFILE]: () => navigateToAdvisorProfile('communication'),
    [OAuthRedirectPage.ADVISOR_ONBOARDING]: () =>
      navigate(`/advisors/${advisorId}/onboarding/complete-profile`),
    [OAuthRedirectPage.HOME]: () => navigate('/'),
  };
  const getNavigationFn = () => getPageToNavigateFn(pageToNavigateFn);
  const { toast } = useToast();

  // Handle OAuth callback
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const callbackError = searchParams.get('error');
    if (callbackError) {
      console.error('OAuth error:', callbackError);
      getNavigationFn()();
    } else {
      const code = searchParams.get('code');
      const advisorIdState = searchParams.get('state');
      const searchParamsReady = code && advisorIdState;
      const loadingOrComplete = status === 'loading' || status === 'complete';

      if (searchParamsReady && !loadingOrComplete) {
        dispatch(
          providerConfig.dispatchAction({
            code,
            advisorId: advisorIdState,
          }),
        );
      }
    }
  }, [location.search, providerConfig, status]);

  // Handle success
  useEffect(() => {
    if (status === 'complete' && !session.isLoading && !redirectInitiated) {
      dispatch(fetchSessionForAuthRequest()); // refresh session
      dispatch(setOauthRedirectInitiated(true));
      getNavigationFn()();
      toast({
        title: `${providerConfig.title}`,
        description: `You can now send emails from your ${capitalizeFirstLetter(providerConfig.provider)} account.`,
        variant: 'default',
        className: 'border-green-500 bg-green-50 text-green-800',
      });
    }
  }, [status, session.isLoading]);

  if (status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md space-y-2 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-gray-800">
              <TriangleAlert
                size={24}
                className="text-yellow-600"
              />
              Authentication Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert
              variant="destructive"
              className="mb-4 border-yellow-600"
            >
              <AlertDescription className="text-gray-900">
                {error}
              </AlertDescription>
            </Alert>
            <Button
              variant="default"
              className="w-full"
              onClick={() => getNavigationFn()()}
            >
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md shadow-lg">
        {status === 'complete' ? (
          <CardContent className="pt-6 text-center">
            <div className="flex flex-col items-center gap-4">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <CardTitle>{providerConfig.title}</CardTitle>
              <CardDescription className="md:text-base">
                Redirecting you back...
              </CardDescription>
            </div>
          </CardContent>
        ) : (
          <CardContent className="pt-6 text-center">
            <div className="flex flex-col items-center gap-4">
              <ClipLoader
                color="#3b82f6"
                size={48}
              />
              <CardTitle>Processing Authentication</CardTitle>
              <CardDescription>
                Please wait while we connect your account...
              </CardDescription>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
